import React, { useEffect, useState, useMemo, useCallback } from "react";
import { ReactComponent as CloseIcon } from "../../../Assets/Icons/popupCloseIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { Slider, Modal, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import Cropper from "react-easy-crop";
import { getCroppedImgFile } from "../../../utils/cropImage";
import { offerCreationSchema } from "../../../utils/validationSchemas";
import { useDispatch, useSelector } from "react-redux";
import { createOffer } from "../../../services/offerService";
import { scrollToFirstError } from "../../../utils/utilFunctions";
import {
  uploadFile,
  getS3PublicUrl,
  getS3PublicUrlCandidates,
} from "../../../utils/s3";
import toast from "../../../utils/toast";
import CSVUpload from "./CSVUpload";
import { useOfferFormContext } from "./OfferFormContext";
import { ReactComponent as BlueCrossIcon } from "../../../Assets/Icons/blueCross.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { getOfferEditProgressSaveFlags } from "./offerApprovalHelpers";

const OfferCreation = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  handleSaveAndExit,
  payload,
  isApproved: isApprovedProp,
  validationIssue,
  onValidationIssueHandled,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role,
  );
  const isMerchant = role === "merchant" || role === "MERCHANT";
  const { registerNextAction, setIsNextLoading, setIsNextDisabled } =
    useOfferFormContext();
  const hasHydratedOfferCreation = React.useRef(false);
  const effectiveOfferId = offerData?._id || editOfferData?._id || null;
  const locationStorageKey = effectiveOfferId
    ? `OFFER_LOCATION_STATE_${effectiveOfferId}`
    : null;

  const buildImageId = useCallback((source, fallback = "") => {
    if (!source) return `offer-image-${fallback}`;

    return String(source)
      .replace(/[^a-zA-Z0-9_-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }, []);

  const buildUniqueUploadName = useCallback((filename) => {
    const originalName = String(filename || "offer-image");
    const extensionIndex = originalName.lastIndexOf(".");
    const hasExtension =
      extensionIndex > 0 && extensionIndex < originalName.length - 1;
    const extension = hasExtension ? originalName.slice(extensionIndex) : "";
    const baseName = hasExtension
      ? originalName.slice(0, extensionIndex)
      : originalName;
    const sanitizedBase =
      baseName
        .replace(/[^a-zA-Z0-9_-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "offer-image";
    const uniqueSuffix = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    return `${sanitizedBase}-${uniqueSuffix}${extension}`;
  }, []);

  const createImageItem = useCallback(
    ({ url, s3Url, isThumbnail = false, file = null, fallbackKey = "" }) => ({
      id: buildImageId(
        fallbackKey || `${s3Url || url || "offer-image"}`,
        s3Url || url || fallbackKey,
      ),
      url,
      s3Url: s3Url || null,
      isThumbnail,
      file,
    }),
    [buildImageId],
  );

  // Unified Images State
  // Array of { url: string (local or s3), file: File, isThumbnail: boolean, s3Url: string }
  const getInitialImageList = useCallback(() => {
    // Merge all sources to get the most complete data set
    const data = {
      ...(isEdit ? editOfferData : {}),
      ...(offerData || {}),
      ...(payload || {}),
    };

    if (!data.title && !data.thumbnailImage && !data._id) return [];

    const newImageList = [];
    if (data.thumbnailImage) {
      newImageList.push(
        createImageItem({
          fallbackKey: "thumbnail",
          isThumbnail: true,
          s3Url: data.thumbnailImage,
          url: getS3PublicUrl(data.thumbnailImage),
        }),
      );
    }
    if (data.previewImages?.length > 0) {
      data.previewImages.forEach((p, index) => {
        newImageList.push(
          createImageItem({
            fallbackKey: `preview-${index}`,
            isThumbnail: false,
            s3Url: p,
            url: getS3PublicUrl(p),
          }),
        );
      });
    }
    return newImageList;
  }, [createImageItem, isEdit, editOfferData, offerData, payload]);

  const [imageList, setImageList] = useState(getInitialImageList());
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);
  const [previewUrlForCrop, setPreviewUrlForCrop] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [, setActiveUploads] = useState(0); // aggregate uploading counter

  // Separate loading states for each button
  const [isAddingThumbnail, setIsAddingThumbnail] = useState(false);
  const [isAddingImage, setIsAddingImage] = useState(false);
  const [isSubmittingNext, setIsSubmittingNext] = useState(false);

  const handleS3ImageError = useCallback((event, rawUrl) => {
    const candidates = getS3PublicUrlCandidates(rawUrl);
    const currentIndex = Number(event.currentTarget.dataset.fallbackIndex || 0);
    const nextIndex = currentIndex + 1;
    if (nextIndex < candidates.length) {
      event.currentTarget.dataset.fallbackIndex = String(nextIndex);
      event.currentTarget.src = candidates[nextIndex];
    }
  }, []);

  useEffect(() => {
    if (!previewUrlForCrop) return undefined;

    return () => {
      URL.revokeObjectURL(previewUrlForCrop);
    };
  }, [previewUrlForCrop]);

  useEffect(() => {
    if (
      !isCropModalOpen ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;

    if (!body || !body.style || !documentElement) {
      return undefined;
    }

    const previousWidth = body.style.width;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    body.classList.add("offer-crop-modal-open");
    body.style.width = "100%";
    body.style.paddingRight = `${Math.max(scrollbarWidth, 0)}px`;

    return () => {
      body.classList.remove("offer-crop-modal-open");
      body.style.width = previousWidth;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isCropModalOpen]);

  // Description and discount details are handled directly by formik now.

  const initialValues = useMemo(() => {
    // Smart merge: prioritize payload if it has specific offer creation fields,
    // otherwise ensure defaults from editOfferData or offerData are preserved.
    const data = {
      ...(isEdit ? editOfferData : {}),
      ...(offerData || {}),
      ...(payload || {}),
    };

    // Extract bundled details from description if they exist
    // Pattern (historical): Main Description.\n\nDetail Description
    // Pattern (current): Main Description.\nDetail Description
    let rawDescription = data?.description || "";
    let mainDescription = rawDescription;
    let extractedDetails = "";

    // We only ever want ONE line break between the two parts.
    // Support old offers saved with "\n\n" while moving forward with "\n".
    // If multiple separators exist, treat the LAST chunk as "details".
    // Fix 3: Use a typed separator that is guaranteed never to appear in
    // normal user text so that reconstruction is always unambiguous.
    // '||DETAIL||' is the current format. The old '\n\n' separator is still
    // recognised for backwards compatibility with offers saved before this fix.
    const splitByNewline = (desc) => {
      const normalized = String(desc || "").replace(/\r\n/g, "\n");

      // Current format: typed separator
      const SEPARATOR = "||DETAIL||";
      if (normalized.includes(SEPARATOR)) {
        const idx = normalized.indexOf(SEPARATOR);
        return {
          main: normalized.slice(0, idx).trim(),
          details: normalized.slice(idx + SEPARATOR.length).trim(),
        };
      }

      // Legacy format: blank-line separator (\n\n)
      if (normalized.includes("\n\n")) {
        const parts = normalized.split(/\n\n+/);
        const details = parts.pop() || "";
        return { main: parts.join("\n"), details };
      }

      // No separator found — everything is main description, no extracted detail
      return { main: normalized, details: "" };
    };

    const { main, details } = splitByNewline(rawDescription);
    mainDescription = main;
    extractedDetails = details;

    return {
      title: data?.title || "",
      offerType: data?.offerType || "INSTORE_SWOODLE_OFFER",
      offerDiscountType: data?.offerDiscountType || "",
      onlineOption:
        data?.offerType === "ONLINE_OFFER"
          ? data?.onlineOption || ""
          : undefined,
      offerCodes: data?.offerCodes || [],
      fixedDollarAmount:
        data?.fixedDollarAmount !== null &&
        data?.fixedDollarAmount !== undefined
          ? data.fixedDollarAmount
          : "",
      discountRange:
        data?.discountRange !== undefined ? data.discountRange : 25,
      description: mainDescription,
      freeOfferDetails:
        data?.offerDiscountType === "FREE_OFFER" ? extractedDetails : "",
      bogoDetails:
        data?.offerDiscountType === "BUY_ONE_GET_ONE_FREE"
          ? extractedDetails
          : "",
      bundleOfferDetails:
        data?.offerDiscountType === "BUNDLE_OFFER" ? extractedDetails : "",
      thumbnailImage: data?.thumbnailImage || null,
      previewImages: data?.previewImages || [],
      document: data?.document || "",
      websiteURL: data?.websiteURL || null,
    };
  }, [offerData, editOfferData, payload, isEdit]); // Stable initialValues

  const formik = useFormik({
    initialValues,
    validationSchema: offerCreationSchema(role),
    enableReinitialize: false,

    onSubmit: async (values) => {
      console.log("Form onSubmit called with values:", values);
      console.log("Form errors:", formik.errors);
      console.log("Form touched:", formik.touched);
      await handleNextStep();
    },
  });

  const {
    title,
    offerType,
    onlineOption,
    offerDiscountType,
    discountRange,
    fixedDollarAmount,
    offerCodes,
    websiteURL,
    document,
    freeOfferDetails,
    bogoDetails,
    bundleOfferDetails,
    thumbnailImage,
    previewImages,
  } = formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateForm,
    setFieldError,
    setFieldTouched,
    handleSubmit,
  } = formik;

  const [, setFileLoading] = useState(false);
  const [, setFileUrl] = useState(null);
  const [, setFileData] = useState();

  useEffect(() => {
    hasHydratedOfferCreation.current = false;
  }, [effectiveOfferId]);

  useEffect(() => {
    if (hasHydratedOfferCreation.current) return;

    formik.setValues(initialValues, false);
    setImageList(getInitialImageList());
    hasHydratedOfferCreation.current = true;
  }, [formik, getInitialImageList, initialValues]);
  const progressSaveFlags = useMemo(
    () =>
      getOfferEditProgressSaveFlags({
        role,
        isEdit,
        isApprovedProp,
        editOfferData,
        offerData,
      }),
    [role, isEdit, isApprovedProp, editOfferData, offerData],
  );

  const validateFile = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile === "csv" || extFile === "xlsx" || extFile === "xls") {
      return true;
    } else {
      return false;
    }
  };
  const validateOfferCodeFormat = (code) => {
    const codePattern = /^[a-zA-Z0-9]{6}$/;
    return codePattern.test(code);
  };
  const handleAcceptedFiles = async (event) => {
    const file = event.target.files[0];
    console.log("file", file);
    if (validateFile(file?.name)) {
      setFileLoading(true);

      // Read the CSV file
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split("\n");
        // Remove header row and empty lines, then trim each code
        const codes = lines
          .slice(1) // Skip header row
          .filter((line) => line.trim() !== "") // Remove empty lines
          .map((line) => line.trim()); // Trim whitespace

        if (codes.length > 0) {
          // Validate each code format
          const invalidCodes = codes.filter(
            (code) => !validateOfferCodeFormat(code),
          );

          if (invalidCodes.length > 0) {
            toast.error(
              `Invalid code format found: ${invalidCodes.join(
                ", ",
              )}. Codes must be 6 characters (alphanumeric).`,
            );
            handleRejectedFiles(file);
            setFileLoading(false);
            return;
          }
          setFieldValue("offerCodes", codes);
          setFieldValue("document", file?.name);
        } else {
          toast.error("No valid offer codes found in the CSV file");
          handleRejectedFiles(file);
        }
        setFileLoading(false);
      };
      reader.onerror = () => {
        toast.error("Error reading the CSV file");
        handleRejectedFiles(file);
        setFileLoading(false);
      };
      reader.readAsText(file);
    } else {
      toast.error(`Only .csv, .xls, and .xlsx files are allowed!`);
      handleRejectedFiles(file);
    }
  };

  const handleRejectedFiles = (files) => {
    setFileData(null);
    setFieldValue("document", null);
    files[0]?.errors?.forEach((error) => {
      toast.error(error.message);
    });
  };

  const handleClear = () => {
    setFileUrl(null);
    setFileData(null);
    setFieldValue("document", "");
    setFieldValue("offerCodes", []);
  };

  // Sync imageList back to formik values
  useEffect(() => {
    const thumbnail = imageList.find((img) => img.isThumbnail);
    const previews = imageList
      .filter((img) => !img.isThumbnail)
      .map((img) => img.s3Url)
      .filter(Boolean);

    if (thumbnail?.s3Url !== formik.values.thumbnailImage) {
      setFieldValue("thumbnailImage", thumbnail ? thumbnail.s3Url : null);
    }

    // Deep comparison or simple join comparison to avoid re-renders
    if (previews.join(",") !== (formik.values.previewImages || []).join(",")) {
      setFieldValue("previewImages", previews);
    }
  }, [
    imageList,
    formik.values.thumbnailImage,
    formik.values.previewImages,
    setFieldValue,
  ]);

  const syncImageFields = useCallback(
    (nextImageList) => {
      const nextThumbnail =
        nextImageList.find((img) => img.isThumbnail)?.s3Url || null;
      const nextPreviews = nextImageList
        .filter((img) => !img.isThumbnail)
        .map((img) => img.s3Url)
        .filter(Boolean);

      setFieldValue("thumbnailImage", nextThumbnail);
      setFieldValue("previewImages", nextPreviews);
    },
    [setFieldValue],
  );

  const handleImageRemove = (index, e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    setImageList((prev) => {
      const removedImage = prev[index];
      const nextImageList = prev.filter(
        (_, currentIndex) => currentIndex !== index,
      );

      if (removedImage?.isThumbnail && nextImageList.length > 0) {
        nextImageList[0] = {
          ...nextImageList[0],
          isThumbnail: true,
        };
      }

      syncImageFields(nextImageList);
      return nextImageList;
    });
  };

  const handleSetThumbnail = (index, e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    setImageList((prev) => {
      const newImageList = prev.map((img, i) => ({
        ...img,
        isThumbnail: i === index,
      }));

      syncImageFields(newImageList);
      return newImageList;
    });
  };

  const onDrop = async (acceptedFiles) => {
    if (imageList.length + acceptedFiles.length > 5) {
      toast.error("You can only upload up to 5 images.");
      return;
    }

    for (const file of acceptedFiles) {
      const localUrl = URL.createObjectURL(file);
      setSelectedFileForCrop(file);
      setPreviewUrlForCrop(localUrl);
      setIsCropModalOpen(true);
      break; // Handle one at a time for cropping
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: true,
  });

  const handleCropSave = useCallback(
    async (asThumbnail = false) => {
      if (!selectedFileForCrop || !croppedAreaPixels) return;

      // Set the appropriate loading state
      if (asThumbnail) {
        setIsAddingThumbnail(true);
      } else {
        setIsAddingImage(true);
      }

      setActiveUploads((prev) => prev + 1);
      try {
        // Get the cropped file
        const croppedFile = await getCroppedImgFile(
          previewUrlForCrop,
          croppedAreaPixels,
          0,
          { horizontal: false, vertical: false },
          selectedFileForCrop.name,
        );

        const uniqueUploadName = buildUniqueUploadName(croppedFile.name);
        const s3Url = await uploadFile(croppedFile, uniqueUploadName);
        if (s3Url) {
          const localPreviewUrl = URL.createObjectURL(croppedFile);
          const newItem = createImageItem({
            fallbackKey: uniqueUploadName,
            file: croppedFile,
            isThumbnail: asThumbnail,
            s3Url,
            url: localPreviewUrl,
          });

          setImageList((prev) => {
            const shouldBeThumbnail =
              asThumbnail || !prev.some((img) => img.isThumbnail);
            const normalizedList = shouldBeThumbnail
              ? prev.map((img) => ({ ...img, isThumbnail: false }))
              : prev;

            return [
              ...normalizedList,
              {
                ...newItem,
                isThumbnail: shouldBeThumbnail,
              },
            ].slice(0, 5);
          });
          toast.success("Image added successfully");
        }
      } catch (error) {
        console.error("Crop/Upload error:", error);
        toast.error("Failed to upload image");
      } finally {
        setActiveUploads((prev) => Math.max(0, prev - 1));
        setIsAddingImage(false);
        setIsAddingThumbnail(false);
        setIsCropModalOpen(false);
        setSelectedFileForCrop(null);
        setPreviewUrlForCrop(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    },
    [
      buildUniqueUploadName,
      createImageItem,
      croppedAreaPixels,
      previewUrlForCrop,
      selectedFileForCrop,
    ],
  );

  const processOfferCodes = () => {
    if (typeof offerCodes === "string") {
      const codesArray = offerCodes
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code !== "");
      setFieldValue("offerCodes", codesArray);
      return codesArray;
    }
    return offerCodes;
  };

  const getMainDescription = useCallback(
    (values) => (values?.description || "").trim(),
    [],
  );

  const getDiscountDetailDescription = useCallback((values) => {
    const discountType = values?.offerDiscountType;

    if (discountType === "FREE_OFFER") {
      return (values?.freeOfferDetails || "").trim();
    }

    if (discountType === "BUY_ONE_GET_ONE_FREE") {
      return (values?.bogoDetails || "").trim();
    }

    if (discountType === "BUNDLE_OFFER") {
      return (values?.bundleOfferDetails || "").trim();
    }

    return "";
  }, []);

  const buildOfferDescription = useCallback(
    (values) => {
      const mainDescription = getMainDescription(values);
      const discountDetails = getDiscountDetailDescription(values);
      const DETAIL_SEPARATOR = "||DETAIL||";

      // Always keep the typed separator when discount details exist, even if
      // the main description is empty. Without that, a detail-only save gets
      // reloaded as the main description and the detail textarea appears empty.
      if (!mainDescription && !discountDetails) return "";
      if (!discountDetails) return mainDescription;

      return `${mainDescription}${DETAIL_SEPARATOR}${discountDetails}`;
    },
    [getDiscountDetailDescription, getMainDescription],
  );

  const getSanitizedOnlineOption = (values) => {
    if (values?.offerType !== "ONLINE_OFFER") return undefined;
    const option =
      typeof values?.onlineOption === "string"
        ? values.onlineOption.trim()
        : "";
    return option.length > 0 ? option : undefined;
  };

  const sanitizeDiscountFields = (payload, values) => {
    const sanitized = { ...payload };
    const discountType = values?.offerDiscountType;

    sanitized.offerDiscountType = discountType ? discountType : null;

    if (discountType !== "DISCOUNTED_PERCENT") {
      sanitized.discountRange = null;
    }

    if (discountType !== "FIXED_DOLLAR") {
      sanitized.fixedDollarAmount = null;
    }

    if (discountType !== "FREE_OFFER") {
      sanitized.freeOfferDetails = null;
    }

    if (discountType !== "BUY_ONE_GET_ONE_FREE") {
      sanitized.bogoDetails = null;
    }

    if (discountType !== "BUNDLE_OFFER") {
      sanitized.bundleOfferDetails = null;
    }

    // FIX B: Send explicit null for non-ONLINE offers so the Mongoose validator
    // accepts it (it requires null | undefined for non-online). An empty string
    // fails the validator with "Invalid online option for the selected offer type".
    if (values?.offerType !== "ONLINE_OFFER") {
      sanitized.onlineOption = null;
    }

    return sanitized;
  };

  const handleNextStep = async () => {
    setIsSubmittingNext(true);
    try {
      const processedCodes = processOfferCodes();

      const currentPayload = {
        ...formStatePayload,
        offerCodes: processedCodes,
        description: buildOfferDescription(formik.values),
      };

      const sanitizedOnlineOption = getSanitizedOnlineOption(formik.values);
      if (sanitizedOnlineOption) {
        currentPayload.onlineOption = sanitizedOnlineOption;
      } else {
        delete currentPayload.onlineOption;
      }

      const sanitizedCurrentPayload = sanitizeDiscountFields(
        currentPayload,
        formik.values,
      );

      const currentOfferId = offerData?._id || editOfferData?._id;

      // Update parent state with the bundled version for subsequent steps (Preview/Review)
      setPayloadData(sanitizedCurrentPayload);

      if (isEdit) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        setStepIndex((prev) => prev + 1);
        return;
      }

      const createPromise = await dispatch(
        createOffer({
          ...sanitizedCurrentPayload,
          offerId: currentOfferId,
          ...progressSaveFlags,
        }),
      )?.unwrap();
      const isCreateSuccess =
        createPromise?.success === true ||
        (createPromise && createPromise?.success === undefined);
      if (isCreateSuccess) {
        console.log("createOffer successful, moving to next step");
        setStepIndex((prev) => prev + 1);
      } else {
        console.log("createOffer failed:", createPromise);
      }
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.message ||
        (typeof error === "string" ? error : "Unable to save offer details.");
      console.error("Offer creation submit error:", error);
      toast.error(
        typeof message === "string" ? message : "Unable to save offer details.",
      );
    } finally {
      setIsSubmittingNext(false);
    }
  };

  const formStatePayload = useMemo(() => {
    const sanitizedOnlineOption = getSanitizedOnlineOption(formik.values);

    return {
      id: editOfferData?._id || offerData?._id,
      merchantId: selectedMerchant?._id,
      ...formik.values,
      // Persist the detail textarea inside the saved description so edit mode
      // can reconstruct the discount detail fields on refetch.
      description: buildOfferDescription(formik.values),
      ...(sanitizedOnlineOption ? { onlineOption: sanitizedOnlineOption } : {}),
      ...progressSaveFlags,
    };
  }, [
    offerData?._id,
    selectedMerchant?._id,
    formik.values,
    buildOfferDescription,
    progressSaveFlags,
  ]);

  // B6 fix: Removed redundant first setPayloadData effect here.
  // The reviewFormStatePayload useEffect below writes a superset of the same
  // fields. Having two effects both calling setPayloadData created a race where
  // whichever fired last would win, potentially overwrting fields like websiteURL
  // and onlineOption that reviewFormStatePayload writes but this one did not.

  useEffect(() => {
    if (!validationIssue?.field) return;

    const supportedFields = new Set([
      "title",
      "offerType",
      "description",
      "offerDiscountType",
      "discountRange",
      "fixedDollarAmount",
      "thumbnailImage",
      "previewImages",
      "websiteURL",
      "onlineOption",
      "offerCodes",
      "freeOfferDetails",
      "bogoDetails",
      "bundleOfferDetails",
    ]);

    if (!supportedFields.has(validationIssue.field)) return;

    setFieldTouched(validationIssue.field, true, false);
    setFieldError(validationIssue.field, validationIssue.message);
    scrollToFirstError({ [validationIssue.field]: validationIssue.message }, [
      validationIssue.field,
    ]);
    onValidationIssueHandled?.();
  }, [
    onValidationIssueHandled,
    setFieldError,
    setFieldTouched,
    validationIssue,
  ]);

  // Sync loading state with Layout's Next button
  useEffect(() => {
    setIsNextLoading(Boolean(loading || isSubmittingNext));
  }, [loading, isSubmittingNext, setIsNextLoading]);

  // Update disabled state for Next button
  useEffect(() => {
    const {
      title,
      offerType,
      offerDiscountType,
      websiteURL,
      onlineOption,
      offerCodes,
      description,
      freeOfferDetails,
      bogoDetails,
      bundleOfferDetails,
    } = formik.values;
    const isTitleFilled = !!title?.trim();
    const isOfferTypeSelected = !!offerType;
    const hasThumbnail = imageList.some((img) => img.isThumbnail);
    const hasPreview = imageList.length > 0;

    const isDescriptionValid = !!description?.trim();

    let isOnlineValid = true;
    if (offerType === "ONLINE_OFFER") {
      isOnlineValid = !!websiteURL?.trim() && !!onlineOption;
      if (onlineOption === "ONLINE_WITH_CODE") {
        isOnlineValid = isOnlineValid && offerCodes?.length > 0;
      }
    }

    let isDiscountDetailsValid = true;
    if (offerDiscountType === "FREE_OFFER") {
      isDiscountDetailsValid = !!freeOfferDetails?.trim();
    } else if (offerDiscountType === "BUY_ONE_GET_ONE_FREE") {
      isDiscountDetailsValid = !!bogoDetails?.trim();
    } else if (offerDiscountType === "BUNDLE_OFFER") {
      isDiscountDetailsValid = !!bundleOfferDetails?.trim();
    } else if (offerDiscountType === "FIXED_DOLLAR") {
      isDiscountDetailsValid = !!String(
        formik.values.fixedDollarAmount,
      )?.trim();
    }

    const disabled = !(
      isTitleFilled &&
      isOfferTypeSelected &&
      hasThumbnail &&
      hasPreview &&
      isOnlineValid &&
      isDescriptionValid &&
      isDiscountDetailsValid
    );
    setIsNextDisabled(disabled);
  }, [formik.values, imageList, setIsNextDisabled, isMerchant]);

  const attemptNext = useCallback(async () => {
    const touchedFields = Object.keys(initialValues).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {},
    );
    touchedFields.offerType = true;
    touchedFields.onlineOption = true;
    setTouched(touchedFields);

    const validationErrors = await validateForm();

    // Supplement with custom validation for offerType if necessary
    const errors = { ...validationErrors };
    if (!formik.values.offerType) {
      errors.offerType = "Offer Type Selection is required";
      setFieldError("offerType", "Offer Type Selection is required");
    }

    // Custom check for Thumbnail requirement
    const hasThumbnail = imageList.some((img) => img.isThumbnail);

    // Add validation for Online Option
    if (
      formik.values.offerType === "ONLINE_OFFER" &&
      !formik.values.onlineOption
    ) {
      errors.onlineOption = "Please select an option";
      setFieldError("onlineOption", "Please select an option");
    }

    if (imageList.length > 0 && !hasThumbnail) {
      errors.thumbnailImage = "Please set an image as Cover";
      setFieldError("thumbnailImage", "Please set an image as Cover");
      // Mark as touched so it shows up
      setFieldTouched("thumbnailImage", true);
    }

    if (Object.keys(errors).length > 0) {
      if (errors.websiteURL) {
        toast.error(errors.websiteURL);
      }
      const priority = [
        "title",
        "offerType",
        "offerDiscountType",
        "description",
        "discountRange",
        "fixedDollarAmount",
        "thumbnailImage",
        "previewImages",
        "websiteURL",
        "offerCodes",
      ];
      scrollToFirstError(errors, priority);
      return;
    }
    handleSubmit();
  }, [
    formik.values,
    imageList,
    initialValues,
    setTouched,
    validateForm,
    setFieldError,
    setFieldTouched,
    handleSubmit,
  ]);

  // Register the Next action for the Layout's Next button
  useEffect(() => {
    registerNextAction(() => attemptNext());
  }, [registerNextAction, attemptNext]);

  // validation will be applied inline on Next click for this step

  const reviewFormStatePayload = useMemo(() => {
    // FIX C: Only include onlineOption in the payload for ONLINE_OFFER.
    // For INSTORE offers, send null explicitly so that any stale onlineOption
    // value previously stored in the DB gets cleared on the next save.
    const resolvedOnlineOption =
      offerType === "ONLINE_OFFER" ? onlineOption || null : null;

    return {
      id: editOfferData?._id || offerData?._id,
      merchantId:
        selectedMerchant?._id ||
        editOfferData?.merchant?._id ||
        editOfferData?.merchantId ||
        offerData?.merchant?._id ||
        offerData?.merchantId ||
        null,
      title: title,
      offerType: offerType,
      offerDiscountType: offerDiscountType,
      onlineOption: resolvedOnlineOption,
      discountRange: discountRange,
      fixedDollarAmount: fixedDollarAmount,
      offerCodes: offerCodes,
      thumbnailImage: thumbnailImage,
      previewImages: previewImages,
      description: buildOfferDescription(formik.values),
      document: document,
      websiteURL: websiteURL,
      freeOfferDetails: freeOfferDetails,
      bogoDetails: bogoDetails,
      bundleOfferDetails: bundleOfferDetails,
      ...progressSaveFlags,
    };
  }, [
    offerData?._id,
    offerData?.merchant?._id,
    offerData?.merchantId,
    selectedMerchant?._id,
    editOfferData,
    title,
    offerType,
    offerDiscountType,
    onlineOption,
    discountRange,
    fixedDollarAmount,
    offerCodes,
    thumbnailImage,
    previewImages,
    document,
    websiteURL,
    freeOfferDetails,
    bogoDetails,
    bundleOfferDetails,
    buildOfferDescription,
    formik.values,
    progressSaveFlags,
  ]);

  useEffect(() => {
    setPayloadData(
      sanitizeDiscountFields(reviewFormStatePayload, {
        offerType: offerType,
        offerDiscountType: offerDiscountType,
      }),
    );
  }, [
    reviewFormStatePayload,
    setPayloadData,
    offerType,
    offerDiscountType,
    editOfferData?.status,
    offerData?.status,
  ]);

  const offerTypeOptions = [
    { label: "In-store Offer", value: "INSTORE_SWOODLE_OFFER" },
    { label: "Online Offer", value: "ONLINE_OFFER" },
  ];

  // Enhanced options with descriptions for the new Card UI
  const offerDiscountTypeOptions = [
    {
      label: "None (No Discount)",
      value: "",
      desc: (
        <>
          Use this offer without a discount type.
          <br />
          <i>Example: when promoting a special offer or featured item.</i>
        </>
      ),
    },
    {
      label: "Discounted Offer",
      value: "DISCOUNTED_PERCENT",
      desc: "Apply a percentage discount on the price.",
    },
    {
      label: "Fixed Dollar Discount",
      value: "FIXED_DOLLAR",
      desc: "Apply a fixed amount off on price.",
    },
    {
      label: "Free Offer",
      value: "FREE_OFFER",
      desc: "Give a product for free.",
    },
    {
      label: "Buy One Get One Free Offer",
      value: "BUY_ONE_GET_ONE_FREE",
      desc: "Offer a free product with purchase.",
    },
    {
      label: "Bundle Offer",
      value: "BUNDLE_OFFER",
      desc: "Offer multiple products together.",
    },
  ];

  const handleTypeChange = (e) => {
    const isOnlineToInstoreSwitch =
      isEdit &&
      offerType === "ONLINE_OFFER" &&
      (e === "INSTORE_SWOODLE_OFFER" || e === "INSTORE_OFFER");

    setFieldValue("offerType", e);
    // Clear fields irrelevant to INSTORE offer
    if (e === "INSTORE_SWOODLE_OFFER" || e === "INSTORE_OFFER") {
      // FIX A: Must be null (not empty string) so Mongoose validator passes for INSTORE offers.
      setFieldValue("onlineOption", null);
      setFieldValue("websiteURL", "");
      setFieldValue("offerCodes", []);
      setFieldValue("document", "");

      if (isOnlineToInstoreSwitch) {
        setPayloadData({
          locations: [],
          tempLocations: [],
          availableAcrossAllStores: false,
          locationStepTouched: true,
        });

        if (locationStorageKey) {
          try {
            sessionStorage.removeItem(locationStorageKey);
          } catch (_) {}
        }
        if (effectiveOfferId) {
          try {
            sessionStorage.removeItem(
              `OFFER_LOCATION_SESSION_ACTIVE_${effectiveOfferId}`,
            );
          } catch (_) {}
        }
      }

      if (stepIndex > 5) {
        setStepIndex(6);
      }
    }
  };

  const handleDiscountTypeChange = (value) => {
    setFieldValue("offerDiscountType", value);
    // Clear fields irrelevant to the selected discount type
    if (!value) {
      setFieldValue("discountRange", 25);
      setFieldValue("fixedDollarAmount", "");
      setFieldValue("freeOfferDetails", "");
      setFieldValue("bogoDetails", "");
      setFieldValue("bundleOfferDetails", "");
    } else if (value === "DISCOUNTED_PERCENT") {
      setFieldValue("fixedDollarAmount", "");
      setFieldValue("freeOfferDetails", "");
      setFieldValue("bogoDetails", "");
      setFieldValue("bundleOfferDetails", "");
    } else if (value === "FIXED_DOLLAR") {
      setFieldValue("discountRange", 25);
      setFieldValue("freeOfferDetails", "");
      setFieldValue("bogoDetails", "");
      setFieldValue("bundleOfferDetails", "");
    } else if (
      value === "FREE_OFFER" ||
      value === "BUY_ONE_GET_ONE_FREE" ||
      value === "BUNDLE_OFFER"
    ) {
      setFieldValue("fixedDollarAmount", "");
      setFieldValue("discountRange", 25);
      if (value !== "FREE_OFFER") setFieldValue("freeOfferDetails", "");
      if (value !== "BUY_ONE_GET_ONE_FREE") setFieldValue("bogoDetails", "");
      if (value !== "BUNDLE_OFFER") setFieldValue("bundleOfferDetails", "");
    }
  };

  // const radioOptions = ["Featured Offers", "Hot Offers"];
  const radioOnlineOptions = [
    {
      title: "Online",
      value: "ONLINE",
      content: "Users will be redirected to your website to claim this offer.",
    },
    {
      title: "Online with Code",
      value: "ONLINE_WITH_CODE",
      content: "Users can copy this code and apply it on your website.",
    },
  ];
  const [rangeValue, setRangeValue] = useState(25);
  const [tagInput, setTagInput] = useState("");

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = tagInput.trim().replace(/,$/, "").toUpperCase();
      if (val) {
        if (!/^[A-Z0-9]{6}$/.test(val)) {
          toast.error("Code must be exactly 6 characters.");
          return;
        }
        const currentCodes = Array.isArray(offerCodes) ? offerCodes : [];
        if (!currentCodes.includes(val)) {
          setFieldValue("offerCodes", [...currentCodes, val]);
        }
        setTagInput("");
      }
    } else if (
      e.key === "Backspace" &&
      tagInput === "" &&
      Array.isArray(offerCodes) &&
      offerCodes.length > 0
    ) {
      const newCodes = [...offerCodes];
      newCodes.pop();
      setFieldValue("offerCodes", newCodes);
    }
  };

  const handleRemoveTag = (codeToRemove) => {
    if (Array.isArray(offerCodes)) {
      setFieldValue(
        "offerCodes",
        offerCodes.filter((c) => c !== codeToRemove),
      );
    }
  };

  const handleRangeChange = (value) => {
    const parsedValue = Math.min(Math.max(0, Number(value) || 0), 100);
    setRangeValue(parsedValue);
    setFieldValue("discountRange", parsedValue);
  };

  const handleRangeAfterChange = (value) => {};

  useEffect(() => {
    if (offerDiscountType === "DISCOUNTED_PERCENT") {
      // Initialize the range values when discount type is selected
      const initialRange = discountRange || 25;
      setRangeValue(initialRange);
      setFieldValue("discountRange", initialRange);
    }
  }, [offerDiscountType, discountRange, setFieldValue]);

  return (
    <div className="w-full flex flex-col items-center justify-center mt-[60px] offer-builder-step3">
      <div className="w-full max-w-[1100px] 2xl:max-w-[1450px] relative">
        <div className="w-full max-w-[650px] mx-auto offerform">
          <h2 className="text-[36px] font-[600] text-[#303030] font-poppins text-left">
            Create Your <span className="text-[#00ADEF]">Offer</span>
          </h2>
          <p className="font-poppins font-[400] text-[18px] text-[#767676] text-left mt-1 mb-10">
            Share the essential details to help customers understand your offer.
          </p>

          {/* 1. Offer Title */}
          {/* 1. Offer Title */}
          <div className="mt-5 w-full">
            <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-[4px]">
              Offer Title<span className="text-red-500">*</span>
            </label>
            <p className="font-poppins font-[400] text-[14px] text-[#767676] mt-1 mb-4">Choose a clear title that tells customers exactly which product or service they can redeem. This will appear as the offer’s headline.</p>
            <input
              type="text"
              name="title"
              value={title}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={70}
              placeholder="Enter offer title"
              className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
            />
            {errors.title && touched.title && (
              <div className="ms-1 text-start w-full">
                <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                  {errors.title}
                </span>
              </div>
            )}
          </div>

          {/* 2. Offer Description */}
          <div className="mt-5 w-full">
            <div className="flex items-center justify-between mb-[4px]">
              <label className="font-[600] text-[#303030] font-poppins text-[14px]">
                Offer Description<span className="text-red-500">*</span>
              </label>
            </div>
            <input
              type="text"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter offer description"
              className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
            />
            {errors.description && touched.description && (
              <div className="ms-1 text-start w-full">
                <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                  {errors.description}
                </span>
              </div>
            )}
          </div>

          {/* 3. Offer Images (Unified) */}
          <div className="mt-8">
            <p className="font-poppins font-[600] text-[14px] text-[#303030]">
              Offer Images<span className="text-red-500">*</span>
            </p>
            <p className="font-poppins font-[400] text-[14px] text-[#767676] mt-1 mb-4">
              To enhance your offer, at least 1 image and 1 cover must be added.
            </p>

            <div
              className="flex flex-wrap gap-4 items-center"
              data-field="thumbnailImage"
            >
              {imageList.map((img, index) => (
                <div
                  key={img.id || img.s3Url || img.url || index}
                  className="relative w-[315px] h-[165px] rounded-[10px] overflow-visible border border-[#D9D9D9] bg-[#F9F9F9] group"
                >
                  <img
                    src={img.url}
                    className="w-full h-full object-cover rounded-[10px]"
                    alt={`Offer ${index}`}
                    data-fallback-index="0"
                    onError={(event) => {
                      if (img.file || String(img.url || "").startsWith("blob:"))
                        return;
                      handleS3ImageError(event, img.s3Url || img.url);
                    }}
                  />
                  {/* Remove button - Cyan Circle with White X positioned on edge */}
                  <button
                    type="button"
                    className="absolute -top-[10px] -right-[10px] w-[28px] h-[28px] bg-[#00BFFF] rounded-full shadow-lg transition-all z-20 hover:scale-110 flex items-center justify-center p-0"
                    onClick={(e) => handleImageRemove(index, e)}
                  >
                    <CloseIcon
                      style={{
                        width: 10,
                        height: 10,
                        color: "white",
                        display: "block",
                        margin: "0",
                      }}
                    />
                  </button>

                  {/* Cover Indicator / Action */}
                  {img.isThumbnail ? (
                    <div className="absolute top-[12px] left-[12px] bg-[#00ADEF] text-white text-[10px] font-poppins px-[10px] py-[4px] z-10 rounded-[5px] font-[400] uppercase">
                      COVER IMAGE
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="absolute top-[12px] left-[12px] bg-white text-[#00ADEF] border border-[#00ADEF] text-[10px] font-poppins px-[10px] py-[4px] z-10 rounded-[5px] font-[400] uppercase hover:bg-[#00ADEF] hover:text-white transition-colors"
                      onClick={(e) => handleSetThumbnail(index, e)}
                    >
                      SET AS COVER
                    </button>
                  )}
                </div>
              ))}

              {/* Render Add Image and One Blank Placeholder */}
              {(() => {
                const results = [];
                const remainingSlots = 5 - imageList.length;

                if (remainingSlots > 0) {
                  // The "Add Image" Card
                  results.push(
                    <div
                      key="add-card"
                      {...getRootProps()}
                      className="w-[315px] h-[165px] border border-solid border-[#D9D9D9] hover:border-[#00ADEF] transition-colors rounded-[10px] flex flex-col items-center justify-center cursor-pointer bg-white"
                    >
                      <input {...getInputProps()} />
                      <PlusOutlined
                        style={{ fontSize: 24, color: "#D9D9D9" }}
                      />
                      <span className="text-[14px] text-[#767676] mt-1 font-poppins">
                        Add Image
                      </span>
                    </div>,
                  );
                }

                return results;
              })()}
            </div>
            {((errors.thumbnailImage &&
              (touched.thumbnailImage || imageList.length > 1)) ||
              (errors.previewImages && touched.previewImages)) && (
              <div className="mt-2 ms-1 text-start">
                <span className="text-red-600 text-[10px] md:text-[12px] font-[600]">
                  {errors.thumbnailImage || errors.previewImages}
                </span>
              </div>
            )}
          </div>

          {/* Cropping / Adjust Modal - Redesigned to match Screenshot 2 */}
          <Modal
            title={null}
            open={isCropModalOpen}
            maskClosable={!(isAddingImage || isAddingThumbnail)}
            keyboard={!(isAddingImage || isAddingThumbnail)}
            closable={!(isAddingImage || isAddingThumbnail)}
            onCancel={() => {
              if (isAddingImage || isAddingThumbnail) return;
              setIsCropModalOpen(false);
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
            closeIcon={
              <div className="offer-modal-close-btn">
                <CloseIcon
                  style={{ width: "14px", height: "14px", color: "white" }}
                />
              </div>
            }
            centered
            width={600}
            className="offer-adjust-image-modal"
            transitionName="ant-fade"
            maskTransitionName="ant-fade"
            footer={null}
          >
            <div className="flex flex-col items-center gap-4 py-6 px-6">
              <h3 className="w-full text-left font-poppins font-[600] text-[20px] text-[#303030] mb-1">
                Adjust Image
              </h3>
              {previewUrlForCrop && (
                <div className="relative w-full h-[300px] rounded-[10px] overflow-hidden bg-[#1a1a1a] shadow-inner">
                  <Cropper
                    image={previewUrlForCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={315 / 165}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
              )}
              <div className="w-full flex items-center gap-6 mt-2">
                <span className="font-poppins text-[14px] font-[500] text-[#767676] -mt-[2px]">
                  Zoom
                </span>
                <Slider
                  className="flex-grow offer-crop-slider"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(v) => setZoom(v)}
                  tooltip={{ open: false }}
                  style={{ margin: 0 }}
                  disabled={isAddingImage || isAddingThumbnail}
                />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 w-full">
                <button
                  className="px-8 py-2 rounded-[5px] text-[#767676] font-poppins font-[500] text-[14px] hover:bg-[#FF0000] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setIsCropModalOpen(false);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                  }}
                  disabled={isAddingImage || isAddingThumbnail}
                >
                  Cancel
                </button>
                <button
                  className="relative px-8 py-2 rounded-[5px] text-[#00ADEF] border border-[#00ADEF] font-poppins font-[500] text-[14px] hover:bg-[#00ADEF] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[120px]"
                  onClick={() =>
                    handleCropSave(!imageList.some((img) => img.isThumbnail))
                  }
                  disabled={isAddingImage || isAddingThumbnail}
                >
                  {isAddingImage || isAddingThumbnail ? (
                    <>
                      <span className="offer-inline-spinner-small absolute left-4" />
                      <span className="text-[#00ADEF] ml-4">Uploading...</span>
                    </>
                  ) : (
                    "Add Image"
                  )}
                </button>
              </div>
            </div>
          </Modal>

          {/* 4. Offer Type */}
          <div className="mt-8 w-full">
            <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-[4px]">
              Offer Type<span className="text-red-500">*</span>
            </p>
            <div className="w-full">
              <Select
                options={offerTypeOptions}
                value={offerType}
                onChange={handleTypeChange}
                placeholder={
                  <span className="text-[#303030]">Select Offer Type</span>
                }
                className="w-full h-[45px] font-poppins text-[#303030] text-[14px] custom-select-offer"
                suffixIcon={<DownArrow className="w-3 h-3 text-[#767676]" />}
                getPopupContainer={(trigger) => trigger.parentNode}
                style={{ borderRadius: "5px" }}
              />
            </div>
          </div>

          {/* 4.5. Online Offer Details (Consolidated Card Design) */}
          {offerType === "ONLINE_OFFER" && (
            <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mt-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] relative z-10 w-full mb-8">
              <h3 className="text-[16px] font-[600] text-[#303030] font-poppins mb-4">
                Online Offer Details
              </h3>

              {/* Website URL Input */}
              <div className="w-full mb-6" onClick={(e) => e.stopPropagation()}>
                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                  Website URL<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="websiteURL"
                  value={websiteURL || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter website URL"
                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                />
                {errors.websiteURL && touched.websiteURL && (
                  <div className="ms-1 text-start w-full">
                    <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                      {errors.websiteURL}
                    </span>
                  </div>
                )}
              </div>

              {/* Online Options Cards */}
              <div className="flex flex-col gap-4">
                {radioOnlineOptions.map((option) => {
                  const isSelected = onlineOption === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`w-full p-4 rounded-[10px] border transition-all flex flex-col items-start gap-4 cursor-pointer ${
                        isSelected
                          ? "border-[#E0E0E0] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.08)]"
                          : "border-[#E0E0E0] bg-white hover:border-[#BDBDBD]"
                      }`}
                      onClick={() =>
                        setFieldValue("onlineOption", option.value)
                      }
                    >
                      <div className="flex items-start gap-4 w-full">
                        <div className="w-[20px] h-[20px] flex-shrink-0 mt-[3.5px] relative">
                          {isSelected ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              className="transition-all duration-300"
                            >
                              <circle cx="10" cy="10" r="10" fill="#00ADEF" />
                              <circle cx="10" cy="10" r="5" fill="white" />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              className="opacity-40 transition-all duration-300"
                            >
                              <circle
                                cx="10"
                                cy="10"
                                r="9"
                                stroke="#00ADEF"
                                stroke-width="2"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex flex-col gap-[4px]">
                          <span className="text-[14px] font-[600] font-poppins text-[#303030]">
                            {option.title}
                          </span>
                          <span className="text-[12px] font-[400] text-[#767676] font-poppins">
                            {option.content}
                          </span>
                        </div>
                      </div>

                      {isSelected && option.value === "ONLINE_WITH_CODE" && (
                        <div
                          className="w-full pl-0 flex flex-col gap-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {document === "" && (
                            <div className="w-full">
                              <div className="offer-tag-input-wrapper">
                                {Array.isArray(offerCodes) &&
                                  offerCodes.map((code, idx) => (
                                    <div key={idx} className="offer-code-tag">
                                      <span>{code}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveTag(code)}
                                      >
                                        <BlueCrossIcon className="w-[18px] h-[18px]" />
                                      </button>
                                    </div>
                                  ))}
                                <input
                                  type="text"
                                  value={tagInput}
                                  onChange={(e) => setTagInput(e.target.value)}
                                  onKeyDown={handleTagKeyDown}
                                  onBlur={() => {
                                    if (tagInput.trim()) {
                                      const val = tagInput
                                        .trim()
                                        .replace(/,$/, "")
                                        .toUpperCase();
                                      if (!/^[a-zA-Z0-9]{6}$/.test(val)) {
                                        toast.error(
                                          "Code must be exactly 6 characters.",
                                        );
                                        return;
                                      }
                                      const currentCodes = Array.isArray(
                                        offerCodes,
                                      )
                                        ? offerCodes
                                        : [];
                                      if (!currentCodes.includes(val)) {
                                        setFieldValue("offerCodes", [
                                          ...currentCodes,
                                          val,
                                        ]);
                                      }
                                      setTagInput("");
                                    }
                                  }}
                                  placeholder={
                                    Array.isArray(offerCodes) &&
                                    offerCodes.length > 0
                                      ? ""
                                      : "Enter 6 Digits Code"
                                  }
                                  className="offer-tag-input-field"
                                  maxLength={6}
                                />
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <InfoIcon className="w-[16px] h-[16px]" />
                                <p className="text-[12px] text-[#767676] font-poppins font-[400]">
                                  Enter multiple codes, separated by commas.
                                </p>
                              </div>
                              {errors.offerCodes && touched.offerCodes && (
                                <div className="ms-1 text-start w-full">
                                  <span className="text-red-600 text-[10px] md:text-[12px] font-[600]">
                                    {errors.offerCodes}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {document === "" && (
                            <div className="flex items-center w-full gap-4">
                              <div className="flex-grow h-[1px] bg-[#E0E0E0]"></div>
                              <span className="text-[#767676] font-poppins text-[14px]">
                                Or
                              </span>
                              <div className="flex-grow h-[1px] bg-[#E0E0E0]"></div>
                            </div>
                          )}

                          <CSVUpload
                            document={document}
                            handleAcceptedFiles={handleAcceptedFiles}
                            handleClear={handleClear}
                            offerCodesCount={
                              Array.isArray(offerCodes) ? offerCodes.length : 0
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {errors.onlineOption && touched.onlineOption && (
                <div className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] mt-1 ms-1">
                  {errors.onlineOption}
                </div>
              )}
            </div>
          )}

          {/* 5. Offer Discount Type (New Card Design) */}
          <div className="mt-8 w-full">
            <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-[4px]">
              Offer Discount Type (Optional)
            </p>
            <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mt-2 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] relative z-10">
              <div className="flex flex-col gap-4">
                {offerDiscountTypeOptions.map((option) => {
                  const isSelected = offerDiscountType === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`offer-discount-card w-full pt-[12px] pb-[4px] px-4 rounded-[6px] border flex items-start gap-[10px] cursor-pointer ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() => handleDiscountTypeChange(option.value)}
                    >
                      {/* 100% Match Radio Button: Top-aligned to stay fixed */}
                      <div className="w-[20px] h-[20px] flex-shrink-0 mt-0 relative">
                        {isSelected ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="transition-all duration-300"
                          >
                            <circle cx="10" cy="10" r="10" fill="#00ADEF" />
                            <circle cx="10" cy="10" r="5" fill="white" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="opacity-40 transition-all duration-300"
                          >
                            <circle
                              cx="10"
                              cy="10"
                              r="9"
                              stroke="#00ADEF"
                              stroke-width="2"
                            />
                          </svg>
                        )}
                      </div>

                      <div className="flex flex-col w-full gap-[11px]">
                        <span className="text-[14px] font-semibold font-poppins text-[#303030]">
                          {option.label}
                        </span>
                        <span className="text-[13px] font-[400] text-[#767676] font-poppins">
                          {option.desc}
                        </span>

                        <div
                          className={`offer-discount-details ${isSelected && ["DISCOUNTED_PERCENT", "FIXED_DOLLAR", "FREE_OFFER", "BUY_ONE_GET_ONE_FREE", "BUNDLE_OFFER"].includes(option.value) ? "expanded" : ""}`}
                        >
                          <div onClick={(e) => e.stopPropagation()}>
                            {option.value === "DISCOUNTED_PERCENT" && (
                              <div className="w-full">
                                <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-1">
                                  Discount Range
                                  <span className="text-red-500">*</span>
                                </p>
                                <div className="">
                                  <Slider
                                    min={0}
                                    max={100}
                                    value={rangeValue}
                                    onChange={handleRangeChange}
                                    onAfterChange={handleRangeAfterChange}
                                    tooltip={{ open: false }}
                                    className="offer-discount-slider"
                                  />
                                </div>
                                {/* Exact Match Percentage Badge below the thumb */}
                                <div className="relative h-[40px] w-full mt-2">
                                  <div
                                    className="absolute"
                                    style={{
                                      left: `${rangeValue}%`,
                                      transform: "translateX(-50%)",
                                    }}
                                  >
                                    <div className="offer-discount-badge">
                                      {rangeValue}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {option.value === "FIXED_DOLLAR" && (
                              <div className="w-full">
                                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                                  Fixed Discount Amount
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  name="fixedDollarAmount"
                                  value={fixedDollarAmount || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val.length > 6) return;
                                    setFieldValue(
                                      "fixedDollarAmount",
                                      val === "" ? "" : Number(val),
                                    );
                                  }}
                                  onBlur={handleBlur}
                                  placeholder="Enter Amount"
                                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                                />
                                {errors.fixedDollarAmount &&
                                  touched.fixedDollarAmount && (
                                    <div className="ms-1 text-start w-full">
                                      <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                                        {errors.fixedDollarAmount}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            )}
                            {option.value === "FREE_OFFER" && (
                              <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                                  Add Details
                                </label>
                                <input
                                  type="text"
                                  name="freeOfferDetails"
                                  value={formik.values.freeOfferDetails}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="Enter detail about this free offer"
                                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                                />
                                {errors.freeOfferDetails &&
                                  touched.freeOfferDetails && (
                                    <div className="ms-1 text-start w-full">
                                      <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                                        {errors.freeOfferDetails}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            )}
                            {option.value === "BUY_ONE_GET_ONE_FREE" && (
                              <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                                  Add Details
                                </label>
                                <input
                                  type="text"
                                  name="bogoDetails"
                                  value={formik.values.bogoDetails}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="Enter details about this buy one get one offer"
                                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                                />
                                {errors.bogoDetails && touched.bogoDetails && (
                                  <div className="ms-1 text-start w-full">
                                    <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                                      {errors.bogoDetails}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {option.value === "BUNDLE_OFFER" && (
                              <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                                  Bundle Offer Details
                                </label>
                                <input
                                  type="text"
                                  name="bundleOfferDetails"
                                  value={formik.values.bundleOfferDetails}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="Enter details about this bundle offer"
                                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                                />
                                {errors.bundleOfferDetails &&
                                  touched.bundleOfferDetails && (
                                    <div className="ms-1 text-start w-full">
                                      <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                                        {errors.bundleOfferDetails}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {errors.offerDiscountType && touched.offerDiscountType && (
              <div
                className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] mt-1 ms-1"
                data-field="offerDiscountType"
              >
                {errors.offerDiscountType}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferCreation;
